import React from 'react';
import Section from '@latitude/section';
import Button from '@latitude/button-rebrand';
import { BUTTON_THEME, REBRAND_COLOR } from '@/utils/constants';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';
import styled from 'styled-components';
import Typography from '@latitude/typography';
import { BREAKPOINT, REBRAND_GRADIENT } from '@latitude/core/utils/constants';
import { getCdrEntryBindingRichTextOptions } from '@/utils/contentfulRichTextBindings';

const PromoBannerWrapper = styled.div`
  .container {
    padding-right: 0;
    padding-left: 30px;

    @media (max-width: ${BREAKPOINT.LG}) {
      padding-left: 24px;
      padding-right: 24px;
    }
  }

  background-color: ${REBRAND_COLOR.WHITE};

  .promo-card-title p {
    color: ${REBRAND_COLOR.BLACK};
  }

  .promo-card-description p {
    color: ${REBRAND_COLOR.BLACK_70};
  }

  &.promo-bg-stone {
    background-color: ${REBRAND_COLOR.STONE};

    .promo-card-title p {
      color: ${REBRAND_COLOR.BLACK};
    }

    .promo-card-description p {
      color: ${REBRAND_COLOR.BLACK_70};
    }
    .promo-card-disclaimer p {
      color: ${REBRAND_COLOR.BLACK_70};
    }
  }

  &.promo-bg-white {
    background-color: ${REBRAND_COLOR.WHITE};

    .promo-card-title p {
      color: ${REBRAND_COLOR.INDIGO};
    }

    .promo-card-description p {
      color: ${REBRAND_COLOR.INDIGO};
    }

    .promo-card-disclaimer p {
      color: ${REBRAND_COLOR.INDIGO};
    }
  }

  &.promo-bg-indigo {
    background-color: ${REBRAND_COLOR.INDIGO};

    .promo-card-title p {
      color: ${REBRAND_COLOR.WHITE};
    }

    .promo-card-description p {
      color: ${REBRAND_COLOR.WHITE};
    }

    .promo-card-disclaimer p {
      color: ${REBRAND_COLOR.WHITE};
    }
  }
  &.promo-bg-gradient-breeze {
    background: ${REBRAND_GRADIENT.BREEZE_BLUE};

    .promo-card-title p {
      color: ${REBRAND_COLOR.INDIGO};
    }

    .promo-card-description p {
      color: ${REBRAND_COLOR.INDIGO};
    }
    .promo-card-disclaimer p {
      color: ${REBRAND_COLOR.INDIGO};
    }
  }
`;

const PromoSection = styled(Section)`
  z-index: 0;
  padding-top: 32px;
  padding-bottom: 42px;

  .section__content {
    max-width: 100%;

    @media (min-width: 576px) {
      max-width: 540px;
    }

    @media (min-width: 768px) {
      max-width: 690px;
    }

    @media (min-width: 992px) {
      max-width: 790px;
    }
  }

  .row {
    display: flex;
    flex-direction: column;
    gap: 30px 0;
  }

  p {
    margin: 0;
  }

  svg {
    cursor: pointer;
  }
`;

const PromoCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    gap: 30px;
  }
`;

const PromoCardImage = styled.div`
  position: relative;
  width: 100%;
  order: ${({ alignment }) => (alignment === 'right' ? 2 : 0)};

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  @media (min-width: 768px) {
    width: 50%;

    img {
      width: auto;
      height: 273px;
      aspect-ratio: 325 / 273;
      object-fit: contain;
    }
  }
`;

const PromoCardContentWrapper = styled.div`
  order: 1;
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 16px;
`;

const PromoActionsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;

  a {
    border-color: ${REBRAND_COLOR.INDIGO} !important;
  }
`;

const Promo = ({
  title,
  promoCards,
  backgroundColor,
  headingSize = 'Heading 3',
  alignment
}) => {
  return (
    <PromoBannerWrapper
      className={`promo-bg-${backgroundColor?.toLowerCase()}`}
    >
      <PromoSection className={`promo-section`}>
        <div className="row">
          {promoCards?.map(promoCard => (
            <PromoCard
              key={promoCard?.id}
              className="col-12 col-sm-12 col-md-12"
            >
              <PromoCardContent
                promoCard={promoCard}
                alignment={alignment}
                headingSize={headingSize}
                backgroundColor={backgroundColor}
              />
            </PromoCard>
          ))}
        </div>
      </PromoSection>
    </PromoBannerWrapper>
  );
};

const PromoCardContent = ({
  promoCard,
  alignment,
  headingSize,
  backgroundColor
}) => {
  const OPTIONS_TITLE = getCdrEntryBindingRichTextOptions({
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node, children) => (
        <Typography.SH1 semibold>{children}</Typography.SH1>
      )
    }
  });

  const OPTIONS_DESCRIPTION = getCdrEntryBindingRichTextOptions({
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node, children) => (
        <Typography.Body>{children}</Typography.Body>
      )
    }
  });
  const RICH_TEXT_OPTIONS = getCdrEntryBindingRichTextOptions();

  const getButtonType = theme => {
    if (theme === 'Outline') return 'secondary-white';
    if (theme === 'Blue') return 'secondary-blue';

    return 'primary';
  };

  function createPanelName(name) {
    if (typeof name === 'string') {
      const s0 = name.trim().toLowerCase();
      const s1 = s0.replace(/\s+/g, '_');
      const s2 = s1.replace(/[^a-zA-Z0-9_-]/g, '');

      return s2;
    }
  }

  return (
    <>
      <PromoCardImage alignment={alignment?.toLowerCase()}>
        <img
          src={promoCard?.image?.file?.url}
          alt={
            promoCard?.image?.description || promoCard?.image?.file?.fileName
          }
        />
      </PromoCardImage>

      <PromoCardContentWrapper>
        <div className="promo-card-title">
          {promoCard?.headingCopy &&
            documentToReactComponents(promoCard?.headingCopy, OPTIONS_TITLE)}
        </div>

        <div className="promo-card-description">
          {promoCard?.description &&
            documentToReactComponents(
              promoCard?.description,
              OPTIONS_DESCRIPTION
            )}
        </div>

        <PromoActionsWrapper>
          {promoCard?.primaryButton?.url && (
            <Button
              variant={getButtonType(promoCard?.primaryButtonTheme)}
              ctaUrl={promoCard?.primaryButton?.url}
              trackId="get-started"
              trackEventData={{
                category: 'cta',
                action: promoCard?.eventAction || 'quote-link',
                location: createPanelName(`${promoCard?.name}primarybtn`),
                label: promoCard?.primaryButton?.text
              }}
              trackProductId={[promoCard?.productId || 'PLAULF-WEB']}
              ctaText={promoCard?.primaryButton?.text}
            />
          )}

          {promoCard?.linkButton?.url && (
            <Typography.LinkLarge
              color={REBRAND_COLOR.INDIGO}
              theme={
                backgroundColor === 'Dark'
                  ? BUTTON_THEME.LINKINVERSE
                  : BUTTON_THEME.LINK
              }
              href={promoCard?.linkButton?.url}
              trackId="get-started"
              trackEventData={{
                category: 'cta',
                action: promoCard?.eventAction || 'quote-link',
                location: createPanelName(`${promoCard?.name}linkbtn`),
                label: promoCard?.linkButton?.text
              }}
              trackProductId={[promoCard?.productId || 'PLAULF-WEB']}
            >
              {promoCard?.linkButton?.text}
            </Typography.LinkLarge>
          )}
        </PromoActionsWrapper>

        {promoCard?.disclaimer && (
          <div className="promo-card-disclaimer">
            {documentToReactComponents(promoCard.disclaimer, RICH_TEXT_OPTIONS)}
          </div>
        )}
      </PromoCardContentWrapper>
    </>
  );
};

export default Promo;
